import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, Maximize, Minimize, ChevronLeft, ChevronRight, Image, WifiOff } from 'lucide-react';
import { albumsApi } from '../api/albums';
import { DEFAULT_SLIDESHOW_CONFIG } from '../types';
import type { Photo, SlideshowConfig, SlideshowCollageLayout, SlideshowPhotoFit } from '../types';
import {
  COLLAGE_SIZES,
  MOSAIC_ARRANGEMENTS,
  MOSAIC_RESHUFFLE_MULTIPLIER,
  MIXED_COLLAGE_DWELL_MULTIPLIER,
  CELL_DELAY_MIN_FACTOR,
  CELL_DELAY_MAX_FACTOR,
  getSlideMotion,
  effectiveFit,
  isCollageSlideFor,
  kenBurnsDrift,
} from '../lib/slideshowPresentation';
import { MachhaCharacter } from '../lib/machhaAsset';
import styles from './SlideshowScreenPage.module.css';

// ── Password prompt ──────────────────────────────────────────────────────────

function PasswordPrompt({
  onAuth,
  error,
  loading,
}: {
  onAuth: (password: string) => void;
  error: string | null;
  loading: boolean;
}) {
  const [password, setPassword] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.trim()) onAuth(password);
  };

  return (
    <div className={styles['dark-bg-fullscreen']}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 340, damping: 28 }}
        className={styles['password-wrapper']}
      >
        <div className={styles['mascot-float']}>
          <MachhaCharacter state="Forbidden" part="body" size={200} />
        </div>

        <form onSubmit={handleSubmit} className={styles['password-form']}>
          <div className={styles['heading-center']}>
            <h1 className={styles['heading-title']}>Slideshow</h1>
            <p className={styles['heading-subtitle']}>Enter the password to view this slideshow</p>
          </div>

          <div className={styles['input-group']}>
            <input
              ref={inputRef}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoComplete="off"
              className={styles['password-input']}
            />

            {error && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className={styles['error-text']}
              >
                {error}
              </motion.p>
            )}

            <button
              type="submit"
              disabled={loading || !password.trim()}
              className={styles['submit-btn']}
            >
              {loading ? 'Authenticating...' : 'Enter Slideshow'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// ── Tunables ─────────────────────────────────────────────────────────────────

/** How often we ask "are there new photos?". This is a count-only probe — it
 *  signs no URLs, so it's cheap enough to run indefinitely. */
const COUNT_POLL_MS = 20_000;

/** Re-walk the album to refresh signed URLs before the 6h server TTL lapses. */
const URL_REFRESH_MS = 5 * 60 * 60 * 1000;

/** Backoff ceiling for a failing connection. We retry forever — a venue display
 *  left running overnight must recover on its own. */
const MAX_BACKOFF_MS = 60_000;

/** Safety valve on the pagination walk. */
const MAX_PAGES = 500;

function sessionKey(albumId: string) {
  return `pm.slideshow.${albumId}`;
}

interface PersistedState {
  anchorId: string | null;
  slideNumber: number;
  wasFullscreen: boolean;
}

function readPersisted(albumId: string): PersistedState | null {
  try {
    const raw = sessionStorage.getItem(sessionKey(albumId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return {
      anchorId: typeof parsed.anchorId === 'string' ? parsed.anchorId : null,
      slideNumber: typeof parsed.slideNumber === 'number' ? parsed.slideNumber : 0,
      wasFullscreen: parsed.wasFullscreen === true,
    };
  } catch {
    return null;
  }
}

// ── Display code entry ───────────────────────────────────────────────────────

/** Shown at /tv, where somebody has typed the host and needs to enter a code. */
function CodePrompt({ onSubmit, error }: { onSubmit: (code: string) => void; error: string | null }) {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className={styles['dark-bg-fullscreen']}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 340, damping: 28 }}
        className={styles['password-wrapper']}
      >
        <form
          className={styles['password-form']}
          onSubmit={(e) => {
            e.preventDefault();
            if (value.trim()) onSubmit(value.trim());
          }}
        >
          <div className={styles['heading-center']}>
            <h1 className={styles['heading-title']}>Enter display code</h1>
            <p className={styles['heading-subtitle']}>
              Find it in the album&rsquo;s display settings.
            </p>
          </div>

          <div className={styles['input-group']}>
            <input
              ref={inputRef}
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value.toUpperCase())}
              placeholder="ABCD-EFGH"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              className={styles['code-input']}
            />
            {error && <p className={styles['error-text']}>{error}</p>}
            <button type="submit" disabled={!value.trim()} className={styles['submit-btn']}>
              Continue
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// ── Transition variants ──────────────────────────────────────────────────────

// ── Slide content ────────────────────────────────────────────────────────────

function photoSrc(photo: Photo): string {
  return photo.originalUrl ?? photo.thumbnailUrl ?? '';
}

/**
 * Photo dimensions aren't stored server-side, so orientation is measured from
 * the decoded image. Cached by photo id at module scope: a slideshow loops, and
 * on a photo's second appearance we already know how to size it, so there's no
 * frame where it's laid out with the wrong fit.
 */
const orientationCache = new Map<string, boolean>();

function usePhotoOrientation(photoId: string) {
  const [isPortrait, setIsPortrait] = useState<boolean | null>(
    () => orientationCache.get(photoId) ?? null,
  );

  // Re-read the cache during render when the photo changes, rather than in an
  // effect — an effect would paint one frame with the previous photo's fit.
  const [seenPhotoId, setSeenPhotoId] = useState(photoId);
  if (photoId !== seenPhotoId) {
    setSeenPhotoId(photoId);
    setIsPortrait(orientationCache.get(photoId) ?? null);
  }

  const handleLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      if (!img.naturalWidth || !img.naturalHeight) return;
      const portrait = img.naturalHeight > img.naturalWidth;
      orientationCache.set(photoId, portrait);
      setIsPortrait(portrait);
    },
    [photoId],
  );

  return { isPortrait, handleLoad };
}

/** Maps the resolved fit onto this page's sizing classes. */
function fitClass(
  fit: SlideshowPhotoFit,
  isPortrait: boolean | null,
  preservePortrait: boolean,
  context: 'slide' | 'cell',
): string {
  return styles[`${context}-${effectiveFit(fit, isPortrait, preservePortrait)}`] ?? '';
}

/**
 * Slow continuous pan + zoom for the duration of the slide. Deterministic per
 * photo id so the same photo always drifts the same way, and only `transform`
 * animates, keeping it GPU-composited.
 */
function KenBurnsImage({
  photo,
  durationSeconds,
  fit,
  preservePortrait,
}: {
  photo: Photo;
  durationSeconds: number;
  fit: SlideshowPhotoFit;
  preservePortrait: boolean;
}) {
  const { isPortrait, handleLoad } = usePhotoOrientation(photo._id);
  const drift = useMemo(() => kenBurnsDrift(photo._id), [photo._id]);

  return (
    <motion.img
      src={photoSrc(photo)}
      alt={photo.originalFilename}
      onLoad={handleLoad}
      className={[styles['slide-image'], fitClass(fit, isPortrait, preservePortrait, 'slide')].join(' ')}
      draggable={false}
      initial={{ scale: 1, x: '0%', y: '0%' }}
      animate={{ scale: 1.09, x: `${drift.x}%`, y: `${drift.y}%` }}
      transition={{ duration: durationSeconds + 1.2, ease: 'linear' }}
    />
  );
}

const COLLAGE_LAYOUT_CLASS: Record<SlideshowCollageLayout, string> = {
  '2-up': 'collage-2up',
  '3-up': 'collage-3up',
  '4-grid': 'collage-4grid',
  mosaic: 'collage-mosaic',
};

/**
 * A collage whose cells refresh independently rather than the whole grid
 * swapping at once. A shared cursor walks the album so every photo still gets
 * screen time, and a photo already visible in another cell is skipped so the
 * same shot never appears twice at once.
 */
function SingleSlideImage({
  photo,
  fit,
  preservePortrait,
}: {
  photo: Photo;
  fit: SlideshowPhotoFit;
  preservePortrait: boolean;
}) {
  const { isPortrait, handleLoad } = usePhotoOrientation(photo._id);
  return (
    <img
      src={photoSrc(photo)}
      alt={photo.originalFilename}
      onLoad={handleLoad}
      className={[styles['slide-image'], fitClass(fit, isPortrait, preservePortrait, 'slide')].join(' ')}
      draggable={false}
    />
  );
}

function CollageCellImage({
  photo,
  fit,
  preservePortrait,
}: {
  photo: Photo;
  fit: SlideshowPhotoFit;
  preservePortrait: boolean;
}) {
  const { isPortrait, handleLoad } = usePhotoOrientation(photo._id);
  return (
    <motion.img
      src={photoSrc(photo)}
      alt={photo.originalFilename}
      onLoad={handleLoad}
      className={[styles['collage-image'], fitClass(fit, isPortrait, preservePortrait, 'cell')].join(' ')}
      draggable={false}
      initial={{ opacity: 0, scale: 1.05 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.99 }}
      transition={{ duration: 0.7, ease: 'easeInOut' }}
    />
  );
}

function LiveCollage({
  photos,
  layout,
  intervalSeconds,
  isPlaying,
  seedIndex,
  seedNonce,
  onProgress,
  fit,
  preservePortrait,
}: {
  photos: Photo[];
  layout: SlideshowCollageLayout;
  intervalSeconds: number;
  isPlaying: boolean;
  seedIndex: number;
  /** Bump to force a full re-seed (manual navigation). */
  seedNonce: number;
  onProgress?: (index: number) => void;
  fit: SlideshowPhotoFit;
  preservePortrait: boolean;
}) {
  const cellCount = Math.max(1, Math.min(COLLAGE_SIZES[layout] ?? 4, photos.length));

  const [cells, setCells] = useState<Photo[]>([]);
  const [arrangement, setArrangement] = useState(0);

  const photosRef = useRef<Photo[]>(photos);
  const cellsRef = useRef<Photo[]>([]);
  const cursorRef = useRef(seedIndex);
  const onProgressRef = useRef(onProgress);

  photosRef.current = photos;
  onProgressRef.current = onProgress;

  // Seed (and re-seed on manual nav or a layout change that alters cell count).
  useEffect(() => {
    const list = photosRef.current;
    if (list.length === 0) return;
    cursorRef.current = ((seedIndex % list.length) + list.length) % list.length;
    const seeded = Array.from({ length: cellCount }, (_, i) => list[(cursorRef.current + i) % list.length]);
    cursorRef.current = (cursorRef.current + cellCount - 1) % list.length;
    cellsRef.current = seeded;
    setCells(seeded);
    onProgressRef.current?.(cursorRef.current);
    // seedIndex is intentionally read but not depended on: once seeded, the
    // collage owns its own cursor and must not restart when the anchor moves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cellCount, seedNonce]);

  /** Next photo from the shared cursor, skipping anything already on screen. */
  const pickNext = useCallback((): Photo | null => {
    const list = photosRef.current;
    if (list.length === 0) return null;
    const onScreen = new Set(cellsRef.current.map((c) => c._id));
    for (let attempt = 0; attempt < list.length; attempt++) {
      cursorRef.current = (cursorRef.current + 1) % list.length;
      const candidate = list[cursorRef.current];
      if (!onScreen.has(candidate._id)) return candidate;
    }
    // Album smaller than the grid — repeats are unavoidable.
    return list[cursorRef.current];
  }, []);

  // Independent per-cell timers.
  useEffect(() => {
    if (!isPlaying) return;
    if (photosRef.current.length === 0) return;
    if (photosRef.current.length <= cellCount) return; // nothing new to rotate in

    const baseMs = Math.max(1, intervalSeconds) * 1000;
    const timers: Array<ReturnType<typeof setTimeout>> = [];

    const randomDelay = () =>
      baseMs * (CELL_DELAY_MIN_FACTOR + Math.random() * (CELL_DELAY_MAX_FACTOR - CELL_DELAY_MIN_FACTOR));

    const schedule = (cellIndex: number, delay: number) => {
      timers[cellIndex] = setTimeout(function tick() {
        const picked = pickNext();
        if (picked) {
          cellsRef.current = cellsRef.current.map((c, i) => (i === cellIndex ? picked : c));
          setCells(cellsRef.current);
          onProgressRef.current?.(cursorRef.current);
        }
        timers[cellIndex] = setTimeout(tick, randomDelay());
      }, delay);
    };

    for (let i = 0; i < cellCount; i++) {
      // Fan the first fire out across the interval so cells never bunch up.
      schedule(i, baseMs * (0.4 + i / cellCount) + Math.random() * baseMs * 0.4);
    }

    return () => timers.forEach(clearTimeout);
  }, [cellCount, intervalSeconds, isPlaying, pickNext]);

  // Mosaic re-arrangement, on its own cadence.
  useEffect(() => {
    if (layout !== 'mosaic' || !isPlaying) return;
    const period = Math.max(1, intervalSeconds) * 1000 * MOSAIC_RESHUFFLE_MULTIPLIER;
    const id = setInterval(() => {
      setArrangement((prev) => {
        if (MOSAIC_ARRANGEMENTS.length < 2) return prev;
        let next = prev;
        while (next === prev) next = Math.floor(Math.random() * MOSAIC_ARRANGEMENTS.length);
        return next;
      });
    }, period);
    return () => clearInterval(id);
  }, [layout, intervalSeconds, isPlaying]);

  if (cells.length === 0) return null;

  const placements = layout === 'mosaic' ? MOSAIC_ARRANGEMENTS[arrangement] : null;

  return (
    <div className={[styles['collage-grid'], styles[COLLAGE_LAYOUT_CLASS[layout]]].join(' ')}>
      {cells.map((photo, i) => {
        const placement = placements?.[i];
        return (
          <motion.div
            // Keyed by position, not photo — the cell is a persistent frame that
            // photos move through, so only its contents animate on a swap.
            key={`cell-${i}`}
            layout
            transition={{ type: 'spring', stiffness: 210, damping: 26 }}
            className={styles['collage-cell']}
            style={
              placement
                ? {
                    gridColumn: `${placement.col} / span ${placement.colSpan}`,
                    gridRow: `${placement.row} / span ${placement.rowSpan}`,
                  }
                : undefined
            }
          >
            <AnimatePresence initial={false}>
              <CollageCellImage
                key={photo._id}
                photo={photo}
                fit={fit}
                preservePortrait={preservePortrait}
              />
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
}

// ── Main slideshow component ─────────────────────────────────────────────────

function SlideshowViewer({
  albumId,
  initialConfig,
  onAuthLost,
}: {
  albumId: string;
  initialConfig: SlideshowConfig;
  onAuthLost: () => void;
}) {
  const restored = useMemo(() => readPersisted(albumId), [albumId]);

  const [photos, setPhotos] = useState<Photo[]>([]);
  const [anchorId, setAnchorId] = useState<string | null>(restored?.anchorId ?? null);
  const [slideNumber, setSlideNumber] = useState(restored?.slideNumber ?? 0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [albumName, setAlbumName] = useState('');
  const [eventName, setEventName] = useState('');
  const [config, setConfig] = useState<SlideshowConfig>({ ...DEFAULT_SLIDESHOW_CONFIG, ...initialConfig });
  const [loading, setLoading] = useState(true);
  const [cursorHidden, setCursorHidden] = useState(false);
  const [connectionLost, setConnectionLost] = useState(false);
  const [offerFullscreenResume, setOfferFullscreenResume] = useState(restored?.wasFullscreen ?? false);
  // Bumped on manual navigation to force the live collage to re-seed.
  const [collageNonce, setCollageNonce] = useState(0);
  // Cursor position reported by the live collage, for the photo counter.
  const [collageProgress, setCollageProgress] = useState(0);

  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const photosRef = useRef<Photo[]>([]);
  const knownTotalRef = useRef<number>(-1);
  const failureCountRef = useRef(0);
  const lastIndexRef = useRef(0);
  const walkingRef = useRef(false);

  photosRef.current = photos;

  // ── Photo loading ─────────────────────────────────────────────────────────

  /**
   * Merges an ordered page set into state. Photo objects already held are
   * reused verbatim so their signed URL — and therefore the `<img src>` —
   * stays byte-identical. The old implementation replaced the whole array on
   * every 10s poll with freshly signed URLs, which changed every `src` and made
   * the browser re-download the photo currently on screen six times a minute.
   */
  const mergePhotos = useCallback((incoming: Photo[], preferIncoming = false) => {
    setPhotos((prev) => {
      if (preferIncoming) return incoming;
      const byId = new Map(prev.map((p) => [p._id, p]));
      return incoming.map((p) => byId.get(p._id) ?? p);
    });
  }, []);

  /** Walks every page of the album. Publishes progressively so playback can
   *  start on page one rather than waiting for the whole album. */
  const walkAllPages = useCallback(
    async (preferIncoming = false) => {
      if (walkingRef.current) return;
      walkingRef.current = true;
      try {
        let cursor: string | undefined;
        let pages = 0;
        const collected: Photo[] = [];

        do {
          const data = await albumsApi.getSlideshowPhotos(albumId, cursor);
          collected.push(...data.photos);
          mergePhotos([...collected], preferIncoming && pages === 0 ? true : false);

          setAlbumName(data.albumName);
          setEventName(data.eventName);
          if (data.slideshowConfig) {
            setConfig({ ...DEFAULT_SLIDESHOW_CONFIG, ...data.slideshowConfig });
          }
          knownTotalRef.current = data.totalCount;

          cursor = data.nextCursor ?? undefined;
          pages += 1;
        } while (cursor && pages < MAX_PAGES);

        failureCountRef.current = 0;
        setConnectionLost(false);
        setLoading(false);
      } finally {
        walkingRef.current = false;
      }
    },
    [albumId, mergePhotos],
  );

  /**
   * Recovers in place. The previous implementation called
   * `window.location.reload()` on any failure — a transient network blip
   * restarted the show at photo one AND dropped the browser out of fullscreen,
   * which cannot be re-entered without a user gesture. Nothing here ever
   * reloads; a failing connection just retries with backoff while the current
   * slide stays on screen.
   */
  const handleFetchError = useCallback(
    (err: unknown): number => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        onAuthLost();
        return 0;
      }
      failureCountRef.current += 1;
      setConnectionLost(true);
      return Math.min(2000 * 2 ** (failureCountRef.current - 1), MAX_BACKOFF_MS);
    },
    [onAuthLost],
  );

  // Initial load.
  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const run = async () => {
      try {
        await walkAllPages();
      } catch (err) {
        if (cancelled) return;
        const delay = handleFetchError(err);
        if (delay > 0) retryTimer = setTimeout(run, delay);
      }
    };
    run();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [walkAllPages, handleFetchError]);

  // Cheap count probe — only re-walks when the album actually grew.
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const data = await albumsApi.getSlideshowCount(albumId);
        failureCountRef.current = 0;
        setConnectionLost(false);
        if (data.slideshowConfig) {
          setConfig({ ...DEFAULT_SLIDESHOW_CONFIG, ...data.slideshowConfig });
        }
        setAlbumName(data.albumName);
        setEventName(data.eventName);
        if (data.totalCount !== knownTotalRef.current) {
          knownTotalRef.current = data.totalCount;
          await walkAllPages();
        }
      } catch (err) {
        handleFetchError(err);
      }
    }, COUNT_POLL_MS);
    return () => clearInterval(id);
  }, [albumId, walkAllPages, handleFetchError]);

  // Refresh signed URLs well before the server-side TTL expires.
  useEffect(() => {
    const id = setInterval(() => {
      walkAllPages(true).catch(handleFetchError);
    }, URL_REFRESH_MS);
    return () => clearInterval(id);
  }, [walkAllPages, handleFetchError]);

  // ── Slide composition ─────────────────────────────────────────────────────

  /**
   * Position is anchored to a photo *id*, not an array index. With `newest`
   * ordering every new upload shifts the array, so an index-based cursor
   * silently jumped to a different photo each time the album grew.
   */
  const anchorIndex = useMemo(() => {
    if (photos.length === 0) return 0;
    if (anchorId) {
      const found = photos.findIndex((p) => p._id === anchorId);
      if (found >= 0) return found;
    }
    return Math.min(lastIndexRef.current, photos.length - 1);
  }, [anchorId, photos]);

  useEffect(() => {
    lastIndexRef.current = anchorIndex;
  }, [anchorIndex]);

  // Adopt an anchor once photos first arrive (or the restored one vanished).
  useEffect(() => {
    if (photos.length === 0) return;
    if (!anchorId || !photos.some((p) => p._id === anchorId)) {
      setAnchorId(photos[Math.min(anchorIndex, photos.length - 1)]._id);
    }
  }, [photos, anchorId, anchorIndex]);

  /** In 'collage' mode the grid is the whole show: it never advances as a
   *  slide, its cells just keep rotating on their own timers. */
  const isLiveCollageMode = config.mode === 'collage';

  const isCollageSlide = isCollageSlideFor(config, slideNumber);

  const slideSize = useMemo(() => {
    if (!isCollageSlide) return 1;
    return Math.max(1, Math.min(COLLAGE_SIZES[config.collageLayout] ?? 4, photos.length));
  }, [isCollageSlide, config.collageLayout, photos.length]);

  const slidePhotos = useMemo(() => {
    if (photos.length === 0) return [];
    return Array.from({ length: slideSize }, (_, i) => photos[(anchorIndex + i) % photos.length]);
  }, [photos, anchorIndex, slideSize]);

  const go = useCallback(
    (dir: 1 | -1) => {
      const list = photosRef.current;
      if (list.length === 0) return;
      setDirection(dir);
      setSlideNumber((n) => Math.max(0, n + dir));
      const step = dir * slideSize;
      const next = ((anchorIndex + step) % list.length + list.length) % list.length;
      setAnchorId(list[next]._id);
      // Manual nav in a live collage means "show me a different set now".
      if (isLiveCollageMode) setCollageNonce((n) => n + 1);
    },
    [anchorIndex, slideSize, isLiveCollageMode],
  );

  const goRef = useRef(go);
  goRef.current = go;

  // Auto-advance. Deliberately does NOT depend on `photos` — merging new
  // arrivals used to reset the pending timer and stretch the current slide.
  useEffect(() => {
    if (!isPlaying) return;
    if (photosRef.current.length <= 1) return;
    // A live collage drives itself cell-by-cell; there is no slide to advance.
    if (isLiveCollageMode) return;
    const dwell = isCollageSlide ? MIXED_COLLAGE_DWELL_MULTIPLIER : 1;
    const id = setTimeout(() => goRef.current(1), Math.max(1, config.intervalSeconds) * dwell * 1000);
    return () => clearTimeout(id);
  }, [isPlaying, anchorId, slideNumber, config.intervalSeconds, isLiveCollageMode, isCollageSlide]);

  // Prefetch the next slide.
  const prefetchRef = useRef<HTMLImageElement[]>([]);
  useEffect(() => {
    if (photos.length === 0) return;
    const count = Math.min(slideSize + 2, photos.length);
    prefetchRef.current = Array.from({ length: count }, (_, i) => {
      const photo = photos[(anchorIndex + slideSize + i) % photos.length];
      const img = new window.Image();
      img.src = photoSrc(photo);
      return img;
    });
  }, [anchorIndex, slideSize, photos]);

  // ── Session persistence ───────────────────────────────────────────────────

  useEffect(() => {
    try {
      sessionStorage.setItem(
        sessionKey(albumId),
        JSON.stringify({ anchorId, slideNumber, wasFullscreen: isFullscreen }),
      );
    } catch {
      // Private mode / blocked storage — position just won't survive a refresh.
    }
  }, [albumId, anchorId, slideNumber, isFullscreen]);

  // ── Screen wake lock ──────────────────────────────────────────────────────
  // A venue display must not let the TV blank mid-event.
  useEffect(() => {
    type WakeLockSentinel = { release: () => Promise<void> };
    let sentinel: WakeLockSentinel | null = null;
    const nav = navigator as Navigator & {
      wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinel> };
    };

    const acquire = async () => {
      try {
        sentinel = (await nav.wakeLock?.request('screen')) ?? null;
      } catch {
        // Unsupported or denied — nothing to do.
      }
    };
    acquire();

    const onVisible = () => {
      if (document.visibilityState === 'visible') acquire();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      sentinel?.release().catch(() => {});
    };
  }, []);

  // ── Controls / cursor auto-hide ───────────────────────────────────────────

  const resetCursorTimer = useCallback(() => {
    setCursorHidden(false);
    setShowControls(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setCursorHidden(true);
      setShowControls(false);
    }, 3000);
  }, []);

  useEffect(() => {
    resetCursorTimer();
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [resetCursorTimer]);

  useEffect(() => {
    const handler = () => resetCursorTimer();
    window.addEventListener('mousemove', handler);
    window.addEventListener('touchstart', handler);
    return () => {
      window.removeEventListener('mousemove', handler);
      window.removeEventListener('touchstart', handler);
    };
  }, [resetCursorTimer]);

  // ── Fullscreen ────────────────────────────────────────────────────────────

  const enterFullscreen = useCallback(async () => {
    try {
      await containerRef.current?.requestFullscreen();
      setOfferFullscreenResume(false);
    } catch {
      // Requires a user gesture; the resume chip provides one.
    }
  }, []);

  const toggleFullscreen = useCallback(async () => {
    if (!document.fullscreenElement) {
      await enterFullscreen();
    } else {
      try {
        await document.exitFullscreen();
      } catch {
        // Ignore — state syncs from the fullscreenchange listener.
      }
    }
  }, [enterFullscreen]);

  useEffect(() => {
    const handler = () => {
      const active = !!document.fullscreenElement;
      setIsFullscreen(active);
      if (active) setOfferFullscreenResume(false);
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // ── Keyboard ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      switch (e.key) {
        case ' ':
          e.preventDefault();
          setIsPlaying((p) => !p);
          resetCursorTimer();
          break;
        case 'f':
        case 'F':
          toggleFullscreen();
          break;
        case 'ArrowRight':
          // Guarded: `go` no-ops on an empty list. The old handler did
          // `% photos.length` while photos were still loading, producing
          // setCurrentIndex(NaN) and a crash once photos arrived.
          go(1);
          resetCursorTimer();
          break;
        case 'ArrowLeft':
          go(-1);
          resetCursorTimer();
          break;
        case 'Escape':
          if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
          break;
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [go, toggleFullscreen, resetCursorTimer]);

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className={styles['dark-bg-fullscreen']}>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={styles['loading-content']}>
          <div className={styles['spinner']} />
          <p className={styles['loading-text']}>Loading slideshow...</p>
        </motion.div>
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className={styles['dark-bg-fullscreen']}>
        <div className={styles['empty-content']}>
          <Image className={styles['empty-icon']} />
          <p className={styles['empty-text']}>No photos yet. Photos will appear here as they are added.</p>
          <p className={styles['empty-subtext']}>Checking for new photos every 20 seconds...</p>
        </div>
      </div>
    );
  }

  const motionSpec = getSlideMotion(config.transition, direction);
  // The collage is a persistent surface — keying it by its photo ids would
  // remount the whole grid every time a single cell swapped.
  const slideKey = isLiveCollageMode
    ? 'live-collage'
    : isCollageSlide
      ? `collage-${slideNumber}`
      : slidePhotos[0]._id;

  return (
    <div
      ref={containerRef}
      className={styles['viewer-container']}
      style={{ cursor: cursorHidden ? 'none' : 'default' }}
      onMouseMove={resetCursorTimer}
    >
      {/* Layers are stacked, not sequenced — no mode="wait" — so transitions
          genuinely cross rather than playing out one after the other. */}
      <AnimatePresence initial={false}>
        <motion.div
          key={slideKey}
          initial={motionSpec.initial}
          animate={motionSpec.animate}
          exit={motionSpec.exit}
          transition={motionSpec.transition}
          className={styles['slide-frame']}
        >
          {isCollageSlide ? (
            <LiveCollage
              photos={photos}
              layout={config.collageLayout}
              intervalSeconds={config.intervalSeconds}
              isPlaying={isPlaying}
              seedIndex={anchorIndex}
              seedNonce={isLiveCollageMode ? collageNonce : slideNumber}
              onProgress={setCollageProgress}
              fit={config.photoFit}
              preservePortrait={config.preservePortrait}
            />
          ) : config.transition === 'kenburns' ? (
            <KenBurnsImage
              photo={slidePhotos[0]}
              durationSeconds={config.intervalSeconds}
              fit={config.photoFit}
              preservePortrait={config.preservePortrait}
            />
          ) : (
            <SingleSlideImage
              photo={slidePhotos[0]}
              fit={config.photoFit}
              preservePortrait={config.preservePortrait}
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Reconnecting indicator — replaces the old hard page reload. */}
      <AnimatePresence>
        {connectionLost && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className={styles['reconnect-pill']}
          >
            <WifiOff style={{ width: 13, height: 13 }} />
            Reconnecting…
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fullscreen can only be re-entered from a user gesture, so after a
          manual refresh we offer one instead of silently losing the mode. */}
      <AnimatePresence>
        {offerFullscreenResume && !isFullscreen && (
          <motion.button
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            onClick={enterFullscreen}
            className={styles['resume-fullscreen']}
          >
            <Maximize style={{ width: 14, height: 14 }} />
            Resume fullscreen
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showControls && photos.length > 1 && (
          <>
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => {
                go(-1);
                resetCursorTimer();
              }}
              className={styles['nav-btn-prev']}
              aria-label="Previous photo"
            >
              <ChevronLeft className={styles['icon-nav']} />
            </motion.button>
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => {
                go(1);
                resetCursorTimer();
              }}
              className={styles['nav-btn-next']}
              aria-label="Next photo"
            >
              <ChevronRight className={styles['icon-nav']} />
            </motion.button>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showControls && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.25 }}
            className={styles['control-bar']}
          >
            <div className={styles['control-bar-gradient']}>
              <div className={styles['control-bar-inner']}>
                <div className={styles['info-col']}>
                  {config.showAlbumName && albumName && <p className={styles['album-name']}>{albumName}</p>}
                  {config.showEventName && eventName && <p className={styles['event-name']}>{eventName}</p>}
                </div>

                <div className={styles['controls-group']}>
                  <button
                    onClick={() => {
                      setIsPlaying((p) => !p);
                      resetCursorTimer();
                    }}
                    className={styles['control-btn']}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                  >
                    {isPlaying ? <Pause className={styles['icon-sm']} /> : <Play className={styles['play-icon-offset']} />}
                  </button>

                  <button
                    onClick={toggleFullscreen}
                    className={styles['control-btn']}
                    aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                  >
                    {isFullscreen ? <Minimize className={styles['icon-sm']} /> : <Maximize className={styles['icon-sm']} />}
                  </button>
                </div>

                {config.showPhotoCount && (
                  <p className={styles['photo-counter']}>
                    {isCollageSlide ? collageProgress + 1 : anchorIndex + 1} / {photos.length}
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Page wrapper ─────────────────────────────────────────────────────────────

export function SlideshowScreenPage() {
  // Two entry points: the short, typeable /tv/:code that someone enters at a
  // TV, and the original /slideshow/:albumId/:slideshowToken link.
  const { albumId: albumIdParam, slideshowToken, code: codeParam } = useParams<{
    albumId?: string;
    slideshowToken?: string;
    code?: string;
  }>();

  // A code typed at /tv is held here until it resolves.
  const [typedCode, setTypedCode] = useState<string | null>(null);
  const code = codeParam ?? typedCode ?? undefined;

  const [resolvedAlbumId, setResolvedAlbumId] = useState<string | null>(null);
  const [codeInvalid, setCodeInvalid] = useState(false);
  const albumId = albumIdParam ?? resolvedAlbumId ?? undefined;

  const [authenticated, setAuthenticated] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [initialConfig, setInitialConfig] = useState<SlideshowConfig | null>(null);

  // Resolve the code to an album before anything else can happen.
  useEffect(() => {
    if (!code || albumIdParam) return;
    let cancelled = false;
    albumsApi
      .resolveSlideshowCode(code)
      .then((data) => {
        if (!cancelled) setResolvedAlbumId(data.albumId);
      })
      .catch(() => {
        if (!cancelled) setCodeInvalid(true);
      });
    return () => {
      cancelled = true;
    };
  }, [code, albumIdParam]);

  // Probe the cookie with the count-only endpoint. This used to fetch (and
  // sign) every photo in the album purely to find out whether the cookie was
  // still valid, then throw the payload away and let the viewer fetch it again.
  useEffect(() => {
    if (!albumId) return;
    let cancelled = false;
    albumsApi
      .getSlideshowCount(albumId)
      .then((data) => {
        if (cancelled) return;
        setInitialConfig({ ...DEFAULT_SLIDESHOW_CONFIG, ...data.slideshowConfig });
        setAuthenticated(true);
      })
      .catch(() => {
        // Not authenticated yet — the password prompt renders.
      });
    return () => {
      cancelled = true;
    };
  }, [albumId]);

  const handleAuth = async (password: string) => {
    if (!albumId) return;
    const credential = slideshowToken ? { slideshowToken } : code ? { code } : null;
    if (!credential) return;
    setAuthLoading(true);
    setAuthError(null);
    try {
      const result = await albumsApi.slideshowAuth(albumId, password, credential);
      setInitialConfig({ ...DEFAULT_SLIDESHOW_CONFIG, ...result.slideshowConfig });
      setAuthenticated(true);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setAuthError(msg || 'Invalid password');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleAuthLost = useCallback(() => {
    setAuthenticated(false);
    setAuthError('Session expired — please re-enter the password.');
  }, []);

  // Bare /tv — ask for the code rather than showing an error.
  const isCodeRoute = window.location.pathname.startsWith('/tv');
  if (isCodeRoute && !code) {
    return <CodePrompt onSubmit={setTypedCode} error={null} />;
  }

  if (codeInvalid) {
    return (
      <CodePrompt
        onSubmit={(next) => {
          setCodeInvalid(false);
          setResolvedAlbumId(null);
          setTypedCode(next);
        }}
        error="That code doesn&rsquo;t match any display."
      />
    );
  }

  if (!code && (!albumIdParam || !slideshowToken)) {
    return (
      <div className={styles['dark-bg-fullscreen']}>
        <span className={styles['invalid-text']}>Invalid slideshow link.</span>
      </div>
    );
  }

  // Waiting on the code lookup.
  if (!albumId) {
    return (
      <div className={styles['dark-bg-fullscreen']}>
        <div className={styles['loading-content']}>
          <div className={styles['spinner']} />
          <p className={styles['loading-text']}>Finding your display…</p>
        </div>
      </div>
    );
  }

  if (!authenticated || !initialConfig) {
    return <PasswordPrompt onAuth={handleAuth} error={authError} loading={authLoading} />;
  }

  return <SlideshowViewer albumId={albumId} initialConfig={initialConfig} onAuthLost={handleAuthLost} />;
}
